import type {
  Report, ReportSection, ReportAuditEntry, ReportTemplate, TemplateKind,
  NewReportContext, VisualDataResponse,
  CombinedReportSummary, DistributeResult,
  MeResponse, UserRoleAssignment, AddedRole,
} from './types';
import { createApiClient, feedbackSender } from '@higgins/utils';
import { getAccessToken } from './msal';

// Headers, ApiError and the lost-reply handling come from the shared client (#312).
// GM calls a linked Function App, so its base is configurable.
const { json: apiFetch, request } = createApiClient({
  getToken: getAccessToken,
  base: import.meta.env.VITE_API_BASE_URL ?? '/api',
});

// ─── Reports ────────────────────────────────────────────────────────────────

export function fetchReports(): Promise<Report[]> {
  return apiFetch('/reports');
}

export function fetchReport(id: string): Promise<Report & { sections: ReportSection[] }> {
  return apiFetch(`/reports/${id}`);
}

export interface SavePayload {
  sections?: { sectionKey: string; content?: string; content2?: string; content3?: string }[];
  preparedBy?: string;
}

export function saveReport(id: string, payload: SavePayload): Promise<void> {
  return apiFetch(`/reports/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

// populatedFrom names the earlier Published or Distributed report whose commentary seeded
// the new one; null when populateFromPrevious was off, nothing eligible existed for the
// region, or the copy failed — in which case `warning` says so and the report still exists.
export function createReport(ctx: NewReportContext): Promise<{ id: string; populatedFrom: string | null; warning?: string }> {
  return apiFetch('/reports', {
    method: 'POST',
    body: JSON.stringify(ctx),
  });
}

export function publishReport(id: string): Promise<{ id: string; status: string; publishedOn: string; warnings: string[] }> {
  return apiFetch(`/reports/${id}/publish`, { method: 'POST' });
}

export function cloneReport(id: string, month: number, year: number): Promise<{ id: string }> {
  return apiFetch(`/reports/${id}/clone`, {
    method: 'POST',
    body: JSON.stringify({ month, year }),
  });
}

// Creates an editable same-period draft copy of a published report (commentary
// and visual snapshot copied) so it can be corrected and re-published.
export function amendReport(id: string): Promise<{ id: string }> {
  return apiFetch(`/reports/${id}/amend`, { method: 'POST' });
}

export function deleteReport(id: string): Promise<void> {
  return apiFetch(`/reports/${id}`, { method: 'DELETE' });
}

export function fetchReportHistory(id: string): Promise<ReportAuditEntry[]> {
  return apiFetch(`/reports/${id}/history`);
}

// ─── Visual snapshot refresh ───────────────────────────────────────────────────

export interface RefreshVisualsResult {
  capturedOn: string;
  total: number;
  failedCount: number;
  results: { section: string; slot: number; ok: boolean; error?: string }[];
}

export function refreshVisuals(id: string): Promise<RefreshVisualsResult> {
  return apiFetch(`/reports/${id}/refresh-visuals`, { method: 'POST' });
}

// One slot's chart-ready data, mapped server-side from the stored snapshot.
export interface SlotVisual extends Omit<VisualDataResponse, 'data'> {
  data: VisualDataResponse['data'] | null; // null when ok === false
  ok: boolean;
  error?: string;
}

export interface ReportVisualsResponse {
  capturedOn: string | null; // null when no snapshot has been captured yet
  visuals: Record<string, Record<number, SlotVisual>>; // section → slot → data
}

export function fetchReportVisuals(id: string): Promise<ReportVisualsResponse> {
  return apiFetch(`/reports/${id}/visuals`);
}

// ─── Context options ─────────────────────────────────────────────────────────

export interface ContextOptions {
  regions: string[];
  regionBranches: Record<string, string[]>;   // region → branches[]
  branchDivisions: Record<string, string[]>;  // branch → divisions[]
  roles: string[];
}

export function fetchContextOptions(): Promise<ContextOptions> {
  return apiFetch('/context/options');
}

// ─── Exports ─────────────────────────────────────────────────────────────────

export async function fetchExport(id: string, format: 'word' | 'pptx'): Promise<{ url: string }> {
  const res = await request(`/reports/${id}/export/${format}`);

  const contentType = res.headers.get('Content-Type') ?? '';
  const blob = new Blob([await res.arrayBuffer()], { type: contentType });
  return { url: URL.createObjectURL(blob) };
}

// ─── Combined reports ────────────────────────────────────────────────────────

export interface CreateCombinedPayload {
  title: string;
  date: string;
  preparedBy: string;
  intro: string;
  reportIds: string[];
}

export function createCombinedReport(payload: CreateCombinedPayload): Promise<{ id: string }> {
  return apiFetch('/combined', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function fetchCombinedReports(): Promise<CombinedReportSummary[]> {
  return apiFetch('/combined');
}

export function distributeCombinedReport(id: string): Promise<DistributeResult> {
  return apiFetch(`/combined/${id}/distribute`, { method: 'POST' });
}

export async function fetchCombinedExport(id: string): Promise<{ url: string }> {
  const res = await request(`/combined/${id}/export/word`);

  const contentType = res.headers.get('Content-Type') ?? '';
  const blob = new Blob([await res.arrayBuffer()], { type: contentType });
  return { url: URL.createObjectURL(blob) };
}

// ─── Admin (templates) ───────────────────────────────────────────────────────

export function fetchTemplates(): Promise<ReportTemplate[]> {
  return apiFetch('/manage/templates');
}

export async function uploadTemplate(file: File, kind: TemplateKind): Promise<{ id: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', String(kind));
  // The shared client leaves Content-Type to the browser for FormData (multipart boundary).
  return apiFetch('/manage/templates', { method: 'POST', body: form });
}

export function setTemplateActive(id: string, isActive: boolean): Promise<void> {
  return apiFetch(`/manage/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  });
}

export function deleteTemplate(id: string): Promise<void> {
  return apiFetch(`/manage/templates/${id}`, { method: 'DELETE' });
}

// ─── Me (current user) ─────────────────────────────────────────────────────────

export function fetchMe(): Promise<MeResponse> {
  return apiFetch('/me');
}

/** POST /api/feedback — the header's Report a problem / Suggest button (#658). */
export const sendFeedback = feedbackSender(apiFetch);

// ─── Admin (user roles) ────────────────────────────────────────────────────────

export interface DirectoryUser { name: string; email: string; oid: string; }

export function fetchDirectory(): Promise<DirectoryUser[]> {
  return apiFetch('/manage/directory');
}

export function fetchUserRoles(): Promise<UserRoleAssignment[]> {
  return apiFetch('/manage/user-roles');
}

export function assignUserRole(payload: { upn: string; displayName: string; role: AddedRole; oid?: string }): Promise<{ created?: boolean; updated?: boolean; id?: string }> {
  return apiFetch('/manage/user-roles', { method: 'POST', body: JSON.stringify(payload) });
}

export function removeUserRole(id: string): Promise<void> {
  return apiFetch(`/manage/user-roles/${id}`, { method: 'DELETE' });
}

export async function downloadTemplate(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await request(`/manage/templates/${id}/file`);

  // Pull the filename from Content-Disposition if present
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? `template-${id}.docx`;

  const blob = new Blob([await res.arrayBuffer()], {
    type: res.headers.get('Content-Type') ?? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  return { blob, filename };
}
